import type { IconButtonProps } from '@mui/material/IconButton';

import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import MenuList from '@mui/material/MenuList';
import IconButton from '@mui/material/IconButton';
import MenuItem, { menuItemClasses } from '@mui/material/MenuItem';
import Collapse from '@mui/material/Collapse';
import { Settings as SettingsIcon, ExpandMore, ExpandLess } from '@mui/icons-material';

import { useLocation, useNavigate } from 'react-router';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { getUserEntity } from 'common/UserEntityProvider';
import { hasFunctionality } from 'components/authorization/PermissionUtils';
import { ENTITY_TYPE } from 'common/constants';

// ----------------------------------------------------------------------

export type SettingsMenuItem = {
  label: string;
  href: string;
  icon?: React.ReactNode;
  viewScopes?: string[];
  editScopes?: string[];
  viewRoles?: string[];
  editRoles?: string[];
  entityType?: number;
  children?: SettingsMenuItem[];
  identifier?: string; // When provided, uses hasFunctionality() instead of scope check
};

export type SettingsPopoverProps = IconButtonProps & {
  data?: SettingsMenuItem[];
};

export function SettingsPopover({ data = [], sx, ...other }: SettingsPopoverProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const userEntity = getUserEntity();

  const [openPopover, setOpenPopover] = useState<HTMLButtonElement | null>(null);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const handleOpenPopover = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenPopover(event.currentTarget);
  }, []);

  const handleClosePopover = useCallback(() => {
    setOpenPopover(null);
    setExpandedItems({});
  }, []);

  const handleClickItem = useCallback(
    (path: string) => {
      handleClosePopover();
      navigate(path);
    },
    [handleClosePopover, navigate]
  );

  const handleToggleExpand = useCallback((label: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedItems((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  }, []);

  const renderMenuItem = (option: SettingsMenuItem, isChild = false) => {
    const isAllowed =
      !option.entityType ||
      option.entityType === userEntity?.entityType;

    if (!isAllowed) return null;

    if (option.identifier && !hasFunctionality(option.identifier, 'view')) return null;

    const hasChildren = option.children && option.children.length > 0;
    const isExpanded = expandedItems[option.label] || false;

    return (
      <PermissionsGate
        key={option.label}
        identifier={option.identifier}
        viewScopes={option.identifier ? [] : (option.viewScopes || [])}
        editScopes={option.identifier ? [] : (option.editScopes || [])}
        viewRoles={option.viewRoles || []}
        editRoles={option.editRoles || []}
        entityType={option.identifier ? ENTITY_TYPE.ANY : option.entityType}
      >
        <Box>
          <MenuItem
            selected={option.href === pathname}
            onClick={() => handleClickItem(option.href)}
            sx={{
              pl: isChild ? 4 : 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {option.icon && <Box component="span">{option.icon}</Box>}
              {option.label}
            </Box>
            {hasChildren && (
              <Box
                component="span"
                onClick={(e) => handleToggleExpand(option.label, e)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  ml: 1,
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </Box>
            )}
          </MenuItem>
          {hasChildren && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <MenuList disablePadding>
                {option.children!.map((child) => renderMenuItem(child, true))}
              </MenuList>
            </Collapse>
          )}
        </Box>
      </PermissionsGate>
    );
  };

  return (
    <>
      <IconButton
        onClick={handleOpenPopover}
        sx={{
          width: 40,
          height: 40,
          ...sx,
        }}
        {...other}
      >
        <SettingsIcon />
      </IconButton>

      <Popover
        open={!!openPopover}
        anchorEl={openPopover}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { width: 280 },
          },
        }}
      >
        <MenuList
          disablePadding
          sx={{
            p: 1,
            gap: 0.5,
            display: 'flex',
            flexDirection: 'column',
            [`& .${menuItemClasses.root}`]: {
              px: 1,
              gap: 2,
              borderRadius: 0.75,
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
              [`&.${menuItemClasses.selected}`]: {
                color: 'text.primary',
                bgcolor: 'action.selected',
                fontWeight: 'fontWeightSemiBold',
              },
            },
          }}
        >
          {data.map((option) => renderMenuItem(option))}
        </MenuList>
      </Popover>
    </>
  );
}
